/**
 * medicationTiming: where a medication stands today, from its dates (LP-41):
 * the ONE rule every browser surface reads, so a
 * medication a care manager records ahead of time is never treated as one
 * the client takes now:
 *
 *   - 'stopped'        status discontinued, or a stop date on or before
 *                      today (no write happens; it reads as discontinued).
 *   - 'starting_soon'  status active and a start date after today.
 *   - 'current'        status active, started on or before today (or no
 *                      start date), and no stop date or one after today.
 *
 * A stop date on or before today wins over a start date after it: a
 * medication that has stopped is not one the client is about to take.
 *
 * `today` is the practice's calendar day (practiceTodayIso,
 * src/lib/practiceDate.ts; on the record, useToday). Never the device's
 * day and never the server's UTC day.
 *
 * MIRROR: api/_shared/medicationTiming.ts is the server twin; the two are
 * held equal over one table of cases (tests/ui/medicationTiming.test.ts).
 * An archived row (isActive false) is outside this rule: every caller
 * leaves archived rows out first, as it always has.
 */

import type { MedicationRow } from '@/lib/clientRecord';

export type MedicationTiming = 'starting_soon' | 'current' | 'stopped';

/** The fields the rule reads. */
export type MedicationDates = Pick<MedicationRow, 'status' | 'startedOn' | 'endedOn'>;

const DAY = /^\d{4}-\d{2}-\d{2}/;

/** A stored date's calendar day, or null when it holds none. */
function dayOf(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const m = DAY.exec(value);
  return m ? m[0] : null;
}

/** Where the medication stands on `today` (YYYY-MM-DD). */
export function medicationTiming(m: MedicationDates, today: string): MedicationTiming {
  if (m.status !== 'active') return 'stopped';
  const ended = dayOf(m.endedOn);
  if (ended !== null && ended <= today) return 'stopped';
  const started = dayOf(m.startedOn);
  if (started !== null && started > today) return 'starting_soon';
  return 'current';
}

/** Not archived, and current today: what the client takes now. */
export function isCurrentMedication(m: MedicationDates & { isActive: boolean }, today: string): boolean {
  return m.isActive && medicationTiming(m, today) === 'current';
}

/** The day a not-yet-stopped medication stops, when it has one after
 *  today; null otherwise. */
export function stopsOn(m: MedicationDates, today: string): string | null {
  if (medicationTiming(m, today) === 'stopped') return null;
  const ended = dayOf(m.endedOn);
  return ended !== null && ended > today ? ended : null;
}

/** The day a starting-soon medication starts; null for any other. */
export function startsOn(m: MedicationDates, today: string): string | null {
  return medicationTiming(m, today) === 'starting_soon' ? dayOf(m.startedOn) : null;
}

/** One dated change still to come: a start or a stop after today. */
export interface MedicationChange {
  row: MedicationRow;
  change: 'starts' | 'stops';
  on: string;
}

/**
 * Every change still to come among the rows not archived, soonest first
 * (a start before a stop on the same day, then by name): each start
 * date after today and each stop date after today of a medication that
 * has not stopped.
 */
export function upcomingMedicationChanges(rows: readonly MedicationRow[], today: string): MedicationChange[] {
  const out: MedicationChange[] = [];
  for (const row of rows) {
    if (!row.isActive) continue;
    const start = startsOn(row, today);
    if (start !== null) out.push({ row, change: 'starts', on: start });
    const stop = stopsOn(row, today);
    if (stop !== null) out.push({ row, change: 'stops', on: stop });
  }
  return out.sort(
    (a, b) =>
      a.on.localeCompare(b.on) ||
      (a.change === b.change ? 0 : a.change === 'starts' ? -1 : 1) ||
      a.row.name.localeCompare(b.row.name, 'en-US', { sensitivity: 'base' }),
  );
}
